import {
  getSchemaNodeAtPath,
  newEmptyTenderSchema,
  NodeType,
  nodeTypes,
  SchemaNode,
} from "./node";

// DesignTransformer is a function that takes in the source schema, the current running schema,
// and outputs the current running schema after applying the transformation. This function
// *should* actually return the from/to after it also deletes used properties from the input.
// Note that there are no guarantees as to which SchemaNode "to" points to. The Transformer should find
// the appropriate node to attach to.
export type DesignTransformer = (
  from: SchemaNode,
  to: SchemaNode,
) => SchemaNode;

export function Transform(
  input: SchemaNode,
  transformers: DesignTransformer[],
): SchemaNode {
  // for each transformer, apply the transformation
  let output = newEmptyTenderSchema();
  for (const transformer of transformers) {
    // check if transformer is callable as a function
    if (typeof transformer !== "function") {
      throw new Error(`transformer is not a function: ${transformer}`);
    }
    const newOutput = transformer(input, output);
    if (newOutput === undefined || newOutput === null) {
      throw new Error(`Transformer did not return a valid output`);
    }
    output = newOutput;
  }
  return output.root();
}

class ExtendedSchemaNode extends SchemaNode {
  constructor(node: SchemaNode) {
    super(node.type, node.properties, node.parent, node.label);
    this.children = node.children;
  }

  addSchemaChild(
    source: SchemaNode | ExtendedSchemaNode,
    designType: string,
    label: string,
    originatingEDIPath?: string,
    designProperties?: [string, string][],
    properties?: Record<string, unknown>,
  ): ExtendedSchemaNode {
    return addNewSchemaChild(
      source,
      this,
      nodeTypes.Object,
      designType,
      label,
      originatingEDIPath,
      designProperties,
      properties,
    );
  }

  addSchemaChildAndReturn(
    source: SchemaNode | ExtendedSchemaNode,
    nodeType: NodeType = nodeTypes.Object,
    designType: string,
    label: string,
    originatingEDIPath?: string,
    designProperties?: [string, string][],
    properties?: Record<string, unknown>,
  ): ExtendedSchemaNode {
    return addNewSchemaChildAndReturn(
      source,
      this,
      nodeType,
      designType,
      label,
      originatingEDIPath,
      designProperties,
      properties,
    );
  }
}

// GetGroups assumes that node is a top level object, or a EDI "section" like "heading" or "detail"
// This assumes that root is a root node that represents a json-schema tree.
// It returns a dictionary of all the actual properties/segments that exist on the root of the EDI document
// as SchemaNodes.
export function GetGroups(node: SchemaNode): Record<string, SchemaNode> {
  const output: Record<string, SchemaNode> = {};

  // now each property is a "section" like "heading" or "detail"
  // each section should contain a properties object, and we want to return the result
  // of all sections properties

  // Iterate over each child of the root node
  for (const section of node.children) {
    // Iterate over each property of the child node
    for (const segment of section.children) {
      // Append the property to the output list
      const label = segment.label;
      if (label === undefined) {
        continue;
      }
      // TODO: is this right?
      if (segment.properties instanceof SchemaNode) {
        output[label] = segment.properties;
      }
    }
  }

  return output;
}

// addNewChild adds a new node of the given design type. It returns the parent node.
export function addNewSchemaChild(
  source: SchemaNode | ExtendedSchemaNode,
  target: SchemaNode,
  nodeType: NodeType = nodeTypes.Object,
  designType: string,
  label: string,
  originatingEDIPath?: string,
  designProperties?: [string, string][],
  properties?: Record<string, unknown>,
): ExtendedSchemaNode {
  addNewSchemaChildAndReturn(
    source,
    target,
    nodeType,
    designType,
    label,
    originatingEDIPath,
    designProperties,
    properties,
  );
  return new ExtendedSchemaNode(target);
}

// addNewChildAndReturn adds a new node of the given design type. It returns the new node.
export function addNewSchemaChildAndReturn(
  source: SchemaNode | ExtendedSchemaNode,
  target: SchemaNode | ExtendedSchemaNode,
  nodeType: NodeType = nodeTypes.Object,
  designType: string,
  label: string,
  originatingEDIPath?: string,
  designProperties?: [string, string][],
  properties?: Record<string, unknown>,
): ExtendedSchemaNode {
  if (target === undefined) {
    throw new Error("Parent node is undefined");
  }
  if (source === undefined) {
    throw new Error("Source node is undefined");
  }
  // create new node
  const newNode = new SchemaNode(
    nodeType,
    properties ? properties : {},
    target,
    label,
  );

  // set the properties to what was provided as the default
  if (properties !== undefined) {
    newNode.properties = properties;
  }

  // if we're provided an originating EDI path, then copy over the properties and value from that path
  if (originatingEDIPath !== undefined && originatingEDIPath !== "") {
    const root = source.root();
    const desiredSourceNode = getSchemaNodeAtPath(root, originatingEDIPath);
    if (desiredSourceNode === null) {
      // it is necessarily an error if a design requires a path, but it's not in the schema.
      // TODO: audit this decision! do we want it to "best effort" this, or be obvious that it's missing?
      throw new Error(`Could not find target path: ${originatingEDIPath}`);
    }
    // if we found the target node, append the current properties with the target properties
    if (properties === undefined) {
      properties = {};
    }
    // using the targetSchemaNode's properties as the base, add the new properties
    // when copying over, we should overwrite the properties that are in the targetSchemaNode, and not just append
    // the same property twice
    const newProperties = { ...desiredSourceNode.properties };
    // now for each property in the properties, we should overwrite the target properties
    for (const key in properties) {
      newProperties[key] = properties[key];
    }
    // if there are values, copy them as well
    if (desiredSourceNode.value !== undefined) {
      newNode.value = desiredSourceNode.value;
    }
    newNode.properties = newProperties;
    newNode.ediPath = originatingEDIPath;

    // if we found a target node to copy over, we should delete it from the originating data so it no longer is visible
    desiredSourceNode.removeSelf();
  }

  // add the design properties
  if (designProperties !== undefined) {
    // now add the design properties
    designProperties.forEach(([type, value]) => {
      newNode.setDesignValue(type, value);
    });
  }
  // set the json-schema type
  newNode.setDesignValue("type", designType);
  newNode.setDesignType(designType);
  target.children.push(newNode);
  return new ExtendedSchemaNode(newNode);
}
